/**
 * Demo data presets for internal testing.
 * Each preset seeds profiles, configs, states (and optionally crews) into localStorage,
 * then syncs to Supabase via the normal save helpers.
 */
import {
  ChildProfile, ChildConfig, ChildState, SAMPLE_APPS, DEFAULT_OFF_PHONE_EARNERS,
  DAYS_OF_WEEK, GoldTransaction, PendingApproval, RewardRequest, ParentNotification,
} from "./types";
import { saveProfiles, saveConfig, saveState, setPin } from "./store";
import { createCrew } from "./crewStore";
import { supabase } from "@/integrations/supabase/client";

// Helper: seed friendships both in Supabase and the local friends cache
async function seedFriends(
  childId: string,
  childName: string,
  childAvatar: string,
  friends: { id: string; name: string; avatar: string }[],
) {
  const cacheKey = `timeminers-friends-${childId}`;
  const cached = friends.map(f => ({
    id: crypto.randomUUID(),
    childId,
    friendId: f.id,
    friendName: f.name,
    friendAvatar: f.avatar,
    createdAt: new Date().toISOString(),
  }));
  localStorage.setItem(cacheKey, JSON.stringify(cached));

  // Best-effort write to backend so external lookups work too
  try {
    for (const f of friends) {
      await supabase.from("friendships").upsert(
        { child_id: childId, friend_id: f.id, friend_name: f.name, friend_avatar: f.avatar },
        { onConflict: "child_id,friend_id" },
      );
      await supabase.from("friendships").upsert(
        { child_id: f.id, friend_id: childId, friend_name: childName, friend_avatar: childAvatar },
        { onConflict: "child_id,friend_id" },
      );
    }
  } catch (e) {
    console.warn("Friend seed (network) failed; local cache only", e);
  }
}

// Helper: build a day schedule map
const makeDaySchedules = (earn: number, play: number) =>
  Object.fromEntries(DAYS_OF_WEEK.map(d => [d, { earnMinutes: earn, playMinutes: play }]));

// Helper: build a default config for a child
function makeConfig(childId: string, overrides?: Partial<ChildConfig>): ChildConfig {
  return {
    childId,
    apps: [...SAMPLE_APPS],
    ratio: { earnMinutes: 1, playMinutes: 2 },
    daySchedules: makeDaySchedules(1, 2),
    offPhoneEarners: [...DEFAULT_OFF_PHONE_EARNERS],
    screenTimeLimitMinutes: 60,
    rewardItems: [],
    goldPerMinuteTrade: 2,
    goldPerExchange: 1,
    marketplace: {
      enabledCategories: ["food", "entertainment", "sports", "shopping", "creative", "experiences", "education", "gaming"],
      disabledRewardIds: [],
    },
    ...overrides,
  };
}

function makeState(childId: string, overrides?: Partial<ChildState>): ChildState {
  return {
    childId,
    earnedPlayMinutes: 0,
    usedPlayMinutes: 0,
    totalScreenTimeToday: 0,
    sessions: [],
    gold: 0,
    goldTransactions: [],
    streaks: { daily: { lastDate: "", count: 0 }, perActivity: {} },
    purchasedRewards: [],
    pendingApprovals: [],
    rewardRequests: [],
    parentNotifications: [],
    ...overrides,
  };
}

function goldTx(amount: number, desc: string, type: GoldTransaction["type"] = "daily"): GoldTransaction {
  return { type, amount, description: desc, timestamp: new Date().toISOString() };
}

const today = new Date().toISOString().slice(0, 10);

// ─── Preset 1: Fresh Demo Family ───
export function loadFreshDemo() {
  const id = crypto.randomUUID();
  const profiles: ChildProfile[] = [
    { id, name: "Timmy", avatar: "junior-novice-1", ageGroup: "junior", isPaused: false },
  ];
  saveProfiles(profiles);
  setPin("1234");
  localStorage.setItem("screentime-demo-mode", "true");
  saveConfig(makeConfig(id));
  saveState(makeState(id, { gold: 0 }));
}

// ─── Preset 2: Mid Progress Demo Family ───
export function loadMidProgressDemo() {
  const juniorId = crypto.randomUUID();
  const proId = crypto.randomUUID();
  const profiles: ChildProfile[] = [
    { id: juniorId, name: "Rosie", avatar: "junior-silver-3", ageGroup: "junior", isPaused: false },
    { id: proId, name: "Jake", avatar: "pro-silver-5", ageGroup: "pro", isPaused: false },
  ];
  saveProfiles(profiles);
  setPin("1234");
  localStorage.setItem("screentime-demo-mode", "true");

  // Junior child
  saveConfig(makeConfig(juniorId));
  saveState(makeState(juniorId, {
    gold: 320,
    goldTransactions: [
      goldTx(100, "Daily mining bonus"),
      goldTx(120, "Completed Reading streak", "bonus_activity"),
      goldTx(100, "Chores completed", "bonus_activity"),
    ],
    streaks: { daily: { lastDate: today, count: 10 }, perActivity: { Reading: { lastDate: today, count: 8 } } },
    pendingApprovals: [
      { id: crypto.randomUUID(), earnerName: "Exercise", earnerIcon: "🏃", goldAmount: 15, timestamp: new Date().toISOString(), status: "pending" },
    ],
    rewardRequests: [
      {
        id: crypto.randomUUID(), childId: juniorId, name: "Campus 00s Shoes", icon: "👟", status: "pending", timestamp: new Date().toISOString(),
        itemLink: "https://www.adidas.co.uk/campus-00s-shoes/HQ8708.html",
        linkPreview: { title: "Campus 00s Shoes — adidas", image: "https://assets.adidas.com/images/h_840,f_auto,q_auto,fl_lossy,c_fill,g_auto/fbaf991a78bc4896a3e9a7e8025b735e_9366/Campus_00s_Shoes_White_HQ8708_01_standard.jpg", price: "£100.00", url: "https://www.adidas.co.uk/campus-00s-shoes/HQ8708.html" },
        productMeta: { productTitle: "Campus 00s Shoes", productUrl: "https://www.adidas.co.uk/campus-00s-shoes/HQ8708.html", productDomain: "adidas.co.uk", productImage: "https://assets.adidas.com/images/h_840,f_auto,q_auto,fl_lossy,c_fill,g_auto/fbaf991a78bc4896a3e9a7e8025b735e_9366/Campus_00s_Shoes_White_HQ8708_01_standard.jpg", productPriceValue: 100, productCurrency: "GBP", suggestedCoinValue: 1000, priceDetectedAt: new Date().toISOString() },
      },
    ],
  }));

  // Pro child
  saveConfig(makeConfig(proId));
  saveState(makeState(proId, {
    gold: 580,
    goldTransactions: [
      goldTx(200, "Daily mining bonus"),
      goldTx(180, "Duolingo streak", "bonus_activity"),
      goldTx(200, "Music practice", "bonus_activity"),
    ],
    streaks: { daily: { lastDate: today, count: 12 }, perActivity: { Music: { lastDate: today, count: 10 } } },
  }));
}

// ─── Preset 3: Advanced Demo Family ───
export function loadAdvancedDemo() {
  const id1 = crypto.randomUUID();
  const id2 = crypto.randomUUID();
  const profiles: ChildProfile[] = [
    { id: id1, name: "Diamond Dave", avatar: "junior-diamond-2", ageGroup: "junior", isPaused: false },
    { id: id2, name: "Gold Gemma", avatar: "pro-gold-6", ageGroup: "pro", isPaused: false },
  ];
  saveProfiles(profiles);
  setPin("1234");
  localStorage.setItem("screentime-demo-mode", "true");

  saveConfig(makeConfig(id1, {
    rewardItems: [
      { id: "r1", name: "Extra 30min", icon: "⏰", goldCost: 50 },
      { id: "r2", name: "Movie Night", icon: "🎬", goldCost: 200 },
    ],
  }));
  saveState(makeState(id1, {
    gold: 2450,
    goldTransactions: Array.from({ length: 30 }, (_, i) => goldTx(80, `Day ${i + 1} mining`)),
    streaks: { daily: { lastDate: today, count: 35 }, perActivity: { Reading: { lastDate: today, count: 30 }, Chores: { lastDate: today, count: 28 } } },
    purchasedRewards: ["r1"],
    parentNotifications: [
      { id: crypto.randomUUID(), type: "reward_unlocked", childName: "Diamond Dave", rewardName: "Extra 30min", goldSpent: 50, timestamp: new Date().toISOString(), seen: true },
    ],
  }));

  saveConfig(makeConfig(id2));
  saveState(makeState(id2, {
    gold: 1800,
    goldTransactions: Array.from({ length: 20 }, (_, i) => goldTx(90, `Day ${i + 1} mining`)),
    streaks: { daily: { lastDate: today, count: 22 }, perActivity: { Exercise: { lastDate: today, count: 20 } } },
    rewardRequests: [
      {
        id: crypto.randomUUID(), childId: id2, name: "Nintendo Switch Game", icon: "🎮", status: "pending", timestamp: new Date().toISOString(),
        itemLink: "https://www.nintendo.co.uk/Games/Nintendo-Switch-games/The-Legend-of-Zelda-Tears-of-the-Kingdom-1576884.html",
        linkPreview: { title: "The Legend of Zelda: Tears of the Kingdom", image: "https://assets.nintendo.com/image/upload/ar_16:9,c_lpad,w_1240/b_white/f_auto/q_auto/ncom/software/switch/70010000063714/img", price: "£49.99", url: "https://www.nintendo.co.uk/Games/Nintendo-Switch-games/The-Legend-of-Zelda-Tears-of-the-Kingdom-1576884.html" },
        productMeta: { productTitle: "The Legend of Zelda: Tears of the Kingdom", productUrl: "https://www.nintendo.co.uk/Games/Nintendo-Switch-games/The-Legend-of-Zelda-Tears-of-the-Kingdom-1576884.html", productDomain: "nintendo.co.uk", productImage: "https://assets.nintendo.com/image/upload/ar_16:9,c_lpad,w_1240/b_white/f_auto/q_auto/ncom/software/switch/70010000063714/img", productPriceValue: 49.99, productCurrency: "GBP", suggestedCoinValue: 500, priceDetectedAt: new Date().toISOString() },
      },
    ],
  }));
}

// ─── Preset 4: Mining Crew Demo ───
export async function loadCrewDemo() {
  const id1 = crypto.randomUUID();
  const id2 = crypto.randomUUID();
  const profiles: ChildProfile[] = [
    { id: id1, name: "Jane", avatar: "pro-gold-1", ageGroup: "pro", isPaused: false },
    { id: id2, name: "Pete", avatar: "pro-silver-4", ageGroup: "pro", isPaused: false },
  ];
  saveProfiles(profiles);
  setPin("1234");
  localStorage.setItem("screentime-demo-mode", "true");

  saveConfig(makeConfig(id1));
  saveState(makeState(id1, {
    gold: 600,
    goldTransactions: [goldTx(600, "Starting gold")],
    streaks: { daily: { lastDate: today, count: 14 }, perActivity: {} },
  }));

  saveConfig(makeConfig(id2));
  saveState(makeState(id2, {
    gold: 400,
    goldTransactions: [goldTx(400, "Starting gold")],
    streaks: { daily: { lastDate: today, count: 8 }, perActivity: {} },
  }));

  // Create a crew, add second child, set goal, contribute
  const crew = createCrew("DIAMOND DIGGERS", "⛏️", id1, "Jane", "pro-gold-1");
  if (crew) {
    const crewStore = await import("./crewStore");
    crewStore.joinCrew(crew.id, id2, "Pete", "pro-silver-4");
    crewStore.setCrewGoal(crew.id, { icon: "🎮", name: "GAMING NIGHT", category: "gaming", goldRequired: 500 });
    crewStore.contributeGold(crew.id, id1, "Jane", 150);
    crewStore.contributeGold(crew.id, id2, "Pete", 100);
  }
}

// ─── Preset 5: Pro Squad — two pro miners, friends, wallet, live & completed rewards ───
export async function loadProSquadDemo() {
  const daveId = crypto.randomUUID();
  const mayaId = crypto.randomUUID();
  const profiles: ChildProfile[] = [
    // Top profile keeps the diamond-3 avatar but is now Maya
    { id: daveId, name: "Maya", avatar: "pro-diamond-3", ageGroup: "pro", isPaused: false },
    // Bottom profile keeps its slot but is now Dave, a junior miner
    { id: mayaId, name: "Dave", avatar: "junior-gold-4", ageGroup: "junior", isPaused: false },
  ];
  saveProfiles(profiles);
  setPin("1234");
  localStorage.setItem("screentime-demo-mode", "true");

  const nowIso = new Date().toISOString();
  const daysAgo = (n: number) => new Date(Date.now() - n * 86400000).toISOString();

  // Friends (made up external miners) — shared between Dave & Maya plus their own crews
  const sharedFriends = [
    { id: crypto.randomUUID(), name: "Leo", avatar: "pro-gold-2" },
    { id: crypto.randomUUID(), name: "Zara", avatar: "pro-silver-7" },
    { id: crypto.randomUUID(), name: "Finn", avatar: "pro-diamond-5" },
    { id: crypto.randomUUID(), name: "Aria", avatar: "pro-silver-1" },
  ];

  // ── Dave (Diamond, 65-day streak) ──
  saveConfig(makeConfig(daveId));
  saveState(makeState(daveId, {
    gold: 3120,
    goldTransactions: [
      ...Array.from({ length: 30 }, (_, i) => ({ ...goldTx(85, `Day ${i + 1} mining`), timestamp: daysAgo(30 - i) })),
      { ...goldTx(-200, "Stadium Tour", "purchase"), timestamp: daysAgo(4) },
      { ...goldTx(-75, "Child Cinema Ticket", "purchase"), timestamp: daysAgo(2) },
      { ...goldTx(-50, "Dough Balls", "purchase"), timestamp: daysAgo(1) },
    ],
    streaks: {
      daily: { lastDate: today, count: 65 },
      perActivity: { Reading: { lastDate: today, count: 60 }, Exercise: { lastDate: today, count: 45 } },
    },
    purchasedRewards: [
      "Stadium Tour",          // Tottenham
      "Child Cinema Ticket",   // Odeon
      "Dough Balls",           // Pizza Express
    ],
    rewardRequests: [
      // Live / running goal — partially funded
      {
        id: crypto.randomUUID(), childId: daveId, name: "PlayStation 5 Controller", icon: "🎮",
        status: "approved", goldCost: 600, contributedGold: 380, timestamp: daysAgo(6),
        itemLink: "https://www.playstation.com/en-gb/accessories/dualsense-wireless-controller/",
        rewardPreview: { title: "DualSense Wireless Controller", domain: "playstation.com", url: "https://www.playstation.com/en-gb/accessories/dualsense-wireless-controller/" },
        productMeta: { productTitle: "DualSense Wireless Controller", productDomain: "playstation.com", productPriceValue: 59.99, productCurrency: "GBP", suggestedCoinValue: 600 },
      },
      // Completed — ready to claim
      {
        id: crypto.randomUUID(), childId: daveId, name: "LEGO Star Wars Set", icon: "🧱",
        status: "ready_to_claim", goldCost: 450, contributedGold: 450, timestamp: daysAgo(10),
        itemLink: "https://www.lego.com/en-gb/themes/star-wars",
        rewardPreview: { title: "LEGO Star Wars Set", domain: "lego.com", url: "https://www.lego.com/en-gb/themes/star-wars" },
        productMeta: { productTitle: "LEGO Star Wars Set", productDomain: "lego.com", productPriceValue: 44.99, productCurrency: "GBP", suggestedCoinValue: 450 },
      },
    ],
    parentNotifications: [
      { id: crypto.randomUUID(), type: "reward_unlocked", childName: "Maya", rewardName: "LEGO Star Wars Set", goldSpent: 450, timestamp: daysAgo(0), seen: false },
    ],
  }));

  // ── Maya (Gold, 32-day streak) ──
  saveConfig(makeConfig(mayaId));
  saveState(makeState(mayaId, {
    gold: 1640,
    goldTransactions: [
      ...Array.from({ length: 20 }, (_, i) => ({ ...goldTx(75, `Day ${i + 1} mining`), timestamp: daysAgo(20 - i) })),
      { ...goldTx(-120, "Trampoline Park Entry", "purchase"), timestamp: daysAgo(5) },
      { ...goldTx(-35, "Ice Cream Scoop", "purchase"), timestamp: daysAgo(3) },
    ],
    streaks: {
      daily: { lastDate: today, count: 32 },
      perActivity: { Music: { lastDate: today, count: 28 }, Chores: { lastDate: today, count: 25 } },
    },
    purchasedRewards: [
      "Trampoline Park Entry", // Flip Out
      "Ice Cream Scoop",       // Ben & Jerry's
    ],
    rewardRequests: [
      // Live / running goal
      {
        id: crypto.randomUUID(), childId: mayaId, name: "Sketching Tablet", icon: "🎨",
        status: "approved", goldCost: 800, contributedGold: 220, timestamp: daysAgo(3),
        rewardPreview: { title: "Wacom Sketch Tablet", domain: "wacom.com", url: "https://www.wacom.com/" },
        productMeta: { productTitle: "Wacom Sketch Tablet", productDomain: "wacom.com", productPriceValue: 79.99, productCurrency: "GBP", suggestedCoinValue: 800 },
      },
      // Completed — ready to claim
      {
        id: crypto.randomUUID(), childId: mayaId, name: "Concert Ticket", icon: "🎤",
        status: "ready_to_claim", goldCost: 350, contributedGold: 350, timestamp: daysAgo(8),
        rewardPreview: { title: "Live Concert Ticket", domain: "ticketmaster.co.uk", url: "https://www.ticketmaster.co.uk/" },
        productMeta: { productTitle: "Live Concert Ticket", productDomain: "ticketmaster.co.uk", productPriceValue: 35, productCurrency: "GBP", suggestedCoinValue: 350 },
      },
    ],
    parentNotifications: [
      { id: crypto.randomUUID(), type: "reward_unlocked", childName: "Dave", rewardName: "Concert Ticket", goldSpent: 350, timestamp: daysAgo(0), seen: false },
    ],
  }));

  // Seed friendships — both kids friends with each other plus 4 external friends
  await seedFriends(daveId, "Maya", "pro-diamond-3", [
    { id: mayaId, name: "Dave", avatar: "junior-gold-4" },
    ...sharedFriends,
  ]);
  await seedFriends(mayaId, "Dave", "junior-gold-4", [
    { id: daveId, name: "Maya", avatar: "pro-diamond-3" },
    ...sharedFriends,
  ]);

  // Shared mining crew — Maya & Dave working together, ~50% funded
  const crew = createCrew("GOLD DIGGERS", "⛏️", daveId, "Maya", "pro-diamond-3");
  if (crew) {
    const crewStore = await import("./crewStore");
    crewStore.joinCrew(crew.id, mayaId, "Dave", "junior-gold-4");
    crewStore.setCrewGoal(crew.id, {
      icon: "🎢",
      name: "THEME PARK TRIP",
      category: "experiences",
      goldRequired: 1000,
    });
    crewStore.contributeGold(crew.id, daveId, "Maya", 280);
    crewStore.contributeGold(crew.id, mayaId, "Dave", 220);
  }
}

// Demo mode flag — when set, Control Room access skips the PIN gate
export const DEMO_MODE_KEY = "screentime-demo-mode";
export function markDemoMode() {
  localStorage.setItem(DEMO_MODE_KEY, "true");
}
export function isDemoMode(): boolean {
  return localStorage.getItem(DEMO_MODE_KEY) === "true";
}

// Clear all app data
export function clearAllData() {
  const keys = [
    "screentime-profiles", "screentime-configs", "screentime-states",
    "screentime-pin", "screentime-crews", "screentime-crew-membership",
    DEMO_MODE_KEY,
  ];
  keys.forEach(k => localStorage.removeItem(k));
}

export const DEMO_PRESETS = [
  { id: "fresh", label: "Fresh Demo Family", description: "1 child, no gold, no streak, clean slate", icon: "🌱", load: loadFreshDemo },
  { id: "mid", label: "Mid Progress Family", description: "2 kids, some gold & streaks, pending requests", icon: "📊", load: loadMidProgressDemo },
  { id: "advanced", label: "Advanced Family", description: "High gold, long streaks, diamond tier, purchased rewards", icon: "💎", load: loadAdvancedDemo },
  { id: "crew", label: "Mining Crew Demo", description: "2 linked kids, active crew with shared goal", icon: "⛏️", load: loadCrewDemo },
  { id: "prosquad", label: "Pro Squad Family", description: "2 pro miners (Diamond + Gold), friends, wallet items, live & completed goals", icon: "🚀", load: loadProSquadDemo },
] as const;
